<template lang="pug">
#emergency-transport-patient-list(v-cloak)
  .box.d-print-none
    .box-header
      .d-flex.flex-row
        .d-flex.flex-column.flex-fill
        .buttons.d-flex.flex-row-reverse.justify-content-end.flex-wrap(v-if="!isLoading")
          button.btn.add-btn(v-if="isSender" @click="gotoEmergencyTransportPatientRegister()") 新規作成
          button.btn.setting-btn(:class="{ 'mr-2': isSender }" @click="gotoSetting()") 設定
    .box-body
      SendEmergencyTransportPatientTable(
        v-if="isSender"
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage.sync="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onDetail="gotoDetail"
        @onPrint="onPrint"
      )
      ReceiveEmergencyTransportPatientTable(
        v-else
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage.sync="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onChangeConfirmed="changeConfirmed"
        @onDetail="gotoDetail"
        @onPrint="onPrint"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
        //- 検索
        .search-filter.d-flex.mr-1.align-items-center.mr-2
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div(v-if="isSender") ・搬送先病院名
              div(v-else) ・施設名
              div ・{{ isSender ? "利用者名" : "患者名" }}
        //- ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        //- ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  //- loading modal
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore, fontsStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import EmergencyTransportPatientsService, {
  EmergencyTransportPatientModel,
  EmergencyTransportPatientsGetModel,
  EmergencyTransportPatientConfirmedModel
} from "@/commons/services/EmergencyTransportPatients";
import { EmergencyTransportRole } from "@/commons/services/Facilities";
import { EmergencyTransportPatientPdf } from "@/commons/utils/EmergencyTransportPatientPdf";

import PageLoading from "@/pc/basics/PageLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import SendEmergencyTransportPatientTable from "@/pc/containers/emergencyTransportPatients/SendEmergencyTransportPatientTable.vue";
import ReceiveEmergencyTransportPatientTable, {
  ReceiveEmergencyTransportPatientTableItem
} from "@/pc/containers/emergencyTransportPatients/ReceiveEmergencyTransportPatientTable.vue";

export interface FilterSetting {
  searchWord: string;
}

/**
 * 救急搬送一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    HelpIcon,
    SendEmergencyTransportPatientTable,
    ReceiveEmergencyTransportPatientTable
  },
  name: "EmergencyTransportPatientList",
  $_veeValidate: { validator: "new" }
})
export default class EmergencyTransportPatientList extends Vue {
  /** 救急搬送患者サービス */
  @Inject("EmergencyTransportPatientsService") emergencyTransportPatientsService!: EmergencyTransportPatientsService;

  /** 施設役割 (SENDER / RECEIVER) */
  responseType: EmergencyTransportRole = EmergencyTransportRole.SENDER;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** テーブル列名 */
  fields: Array<any> = [];
  /** 各行の情報 */
  items: Array<any> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;

  /** 送信側 1ページに表示する行数 */
  senderPerPage: number = Number(Setting.otherOfficeEmergencyTransportPatients.senderTable.perPage);
  /** 送信側 1ページに表示する行数の選択項目 */
  senderPageOptions: number[] = Setting.otherOfficeEmergencyTransportPatients.senderTable.pageOptions;

  /** 受信側 1ページに表示する行数 */
  receiverPerPage: number = Number(Setting.otherOfficeEmergencyTransportPatients.receiverTable.perPage);
  /** 受信側 1ページに表示する行数の選択項目 */
  receiverPageOptions: number[] = Setting.otherOfficeEmergencyTransportPatients.receiverTable.pageOptions;

  get isSender(): boolean {
    return this.responseType === EmergencyTransportRole.SENDER;
  }

  get perPage(): number {
    return this.isSender ? this.senderPerPage : this.receiverPerPage;
  }

  set perPage(val: number) {
    if (this.isSender) {
      this.senderPerPage = val;
    } else {
      this.receiverPerPage = val;
    }
  }

  get pageOptions(): number[] {
    return this.isSender ? this.senderPageOptions : this.receiverPageOptions;
  }

  /**
   * ### 送信側 1ページあたりの表示行数の変更イベント
   */
  @Watch("senderPerPage")
  changeSenderPerPage(perPage: number): void {
    this.setCookie("emergency-transport-patient-sender-list-per-page", perPage);
    return;
  }

  /**
   * ### 受信側 1ページあたりの表示行数の変更イベント
   */
  @Watch("receiverPerPage")
  changeReceiverPerPage(perPage: number): void {
    this.setCookie("emergency-transport-patient-receiver-list-per-page", perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  private setCookie(cookieName: string, perPage: number): void {
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `${cookieName}=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    const senderPerPage: number | null = this.loadCookie("emergency-transport-patient-sender-list-per-page");
    this.senderPerPage = senderPerPage ? senderPerPage : Number(Setting.otherOfficeEmergencyTransportPatients.senderTable.perPage);

    const receiverPerPage: number | null = this.loadCookie("emergency-transport-patient-receiver-list-per-page");
    this.receiverPerPage = receiverPerPage ? receiverPerPage : Number(Setting.otherOfficeEmergencyTransportPatients.receiverTable.perPage);

    this.restoreFilterSetting();

    await this.initialize();
    return;
  }

  /**
   * ### Cookieを参照してperPageを取得する
   */
  public loadCookie(cookieName: string): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      const perPage: number = Number(cookieMap[cookieName]);
      return isNaN(perPage) ? null : perPage;
    }
    return null;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * ### 画面初期化
   */
  public async initialize(): Promise<void> {
    this.isLoading = true;
    this.items = [];
    this.currentPage = 1;

    await this.createView();

    this.isLoading = false;
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    try {
      const response: EmergencyTransportPatientsGetModel = await this.getEmergencyTransportPatients();

      this.responseType = response.responseType || EmergencyTransportRole.SENDER;
      const isSender = this.responseType === EmergencyTransportRole.SENDER;
      this.fields = this.createFields(isSender);

      if (isSender) {
        const sendPatients = response.sendPatients || [];
        this.rows = sendPatients.length;
        this.items = this.createItems(sendPatients, true);
      } else {
        const receivePatients = response.receivePatients || [];
        this.rows = receivePatients.length;
        this.items = this.createItems(receivePatients, false);
      }
    } catch (error) {
      errorHandle(error, this);
    }

    if (this.$options.name && itemListFilterStore.currentPageMap[this.$options.name] != undefined) {
      this.currentPage = itemListFilterStore.currentPageMap[this.$options.name];
    }

    return;
  }

  /**
   * ### 緊急搬送患者情報を取得する（413エラー時は段階的に取得）
   */
  public async getEmergencyTransportPatients(): Promise<EmergencyTransportPatientsGetModel> {
    let result: EmergencyTransportPatientsGetModel = {
      responseType: EmergencyTransportRole.SENDER,
      sendPatients: [],
      receivePatients: []
    };

    try {
      result = await this.emergencyTransportPatientsService.gets(userStore);
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemError.statusCode == 413) {
        const countModel = await this.emergencyTransportPatientsService.getCount(userStore);
        result.responseType = countModel.responseType;
        const count = Math.max(countModel.sendCount || 0, countModel.receiveCount || 0);
        const pagePerCount = 300;
        const loopEnd = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          const res = await this.emergencyTransportPatientsService.gets(userStore, params);
          if (res) {
            if (res.sendPatients) {
              result.sendPatients = result.sendPatients.concat(res.sendPatients);
            }
            if (res.receivePatients) {
              result.receivePatients = result.receivePatients.concat(res.receivePatients);
            }
          }
        }
      } else {
        throw error;
      }
    }

    return result;
  }

  /**
   * ### 送信側/受信側に応じた列名を作成する
   */
  public createFields(isSender: boolean): Array<any> {
    const fields: Array<any> = [];

    if (isSender) {
      fields.push({ key: "created", label: "送信日時", sortable: false, sortDirection: "desc", class: "created-datetime" });
      fields.push({ key: "targetFacilityName", label: "搬送先病院名", sortable: false, sortDirection: "desc", class: "target-facility-name" });
      fields.push({ key: "name", label: "利用者名", sortable: false, sortDirection: "desc", class: "resident-name" });
      fields.push({ key: "assignedStaffName", label: "担当職員", sortable: false, sortDirection: "desc", class: "assigned-staff-name" });
      fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });
    } else {
      fields.push({ key: "confirmed", label: "状態", sortable: false, sortDirection: "desc", class: "status text-center" });
      fields.push({ key: "created", label: "受信日時", sortable: false, sortDirection: "desc", class: "created-datetime" });
      fields.push({ key: "facilityName", label: "施設名", sortable: false, sortDirection: "desc", class: "facility-name" });
      fields.push({ key: "name", label: "患者名", sortable: false, sortDirection: "desc", class: "patient-name" });
      fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });
    }

    return fields;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(patients: Array<EmergencyTransportPatientModel>, isSender: boolean): Array<any> {
    let items: Array<any> = patients.map((patient) => ({ ...patient }));

    if (!isSender) {
      // 受信側：未確認を最上位表示、受信日時降順に並び替え
      items.sort((a, b) => {
        const aConfirmed = !!a.confirmed;
        const bConfirmed = !!b.confirmed;
        if (aConfirmed !== bConfirmed) {
          return aConfirmed ? 1 : -1;
        }
        return (b.created || 0) - (a.created || 0);
      });
    }

    return items;
  }

  /**
   * ### 確認フラグ変更（受信側）
   */
  public async changeConfirmed(patient: ReceiveEmergencyTransportPatientTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const changedConfirmed = !patient.confirmed;
      const requestBody: EmergencyTransportPatientConfirmedModel = {
        sendFacilityId: patient.facilityId,
        confirmed: changedConfirmed,
        updated: patient.updated
      };
      const response = await this.emergencyTransportPatientsService.changeConfirmed(userStore, patient.id, requestBody);

      // メモリ上の対象データの確認フラグおよび更新日時を更新
      patient.confirmed = changedConfirmed;
      if (response && response.updated) {
        patient.updated = response.updated;
      }

      // 未確認が最上位に来るよう再ソートして反映
      this.items = this.createItems(this.items, false);

      this.$emit("refresh");
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   */
  selectPerPage(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  selectPage(page: number): void {
    this.currentPage = page;
    return;
  }

  /**
   * ### 新規作成ボタン押下
   */
  gotoEmergencyTransportPatientRegister(): void {
    this.$router.push(PcUrl.OtherOfficeEmergencyTransportRegister);
    return;
  }

  /**
   * ### 設定ボタン押下
   */
  gotoSetting(): void {
    this.$router.push(PcUrl.OtherOfficeEmergencyTransportSetting);
    return;
  }

  /**
   * ### 詳細ボタン押下
   */
  gotoDetail(patient: any): void {
    if (!patient) return;
    this.$router.push({
      name: PcUrl.OtherOfficeEmergencyTransportDetail,
      params: {
        facilityId: String(patient.facilityId),
        id: String(patient.id)
      },
      query: !this.isSender ? { targetFacilityId: String(patient.facilityId) } : {}
    });
    return;
  }

  /**
   * ### 印刷ボタン押下
   */
  async onPrint(patient: any): Promise<void> {
    if (!patient) return;
    this.isBusy = true;
    this.loadingMessage = "PDFを作成中...";

    try {
      const queryParams = !this.isSender ? { targetFacilityId: patient.facilityId } : undefined;
      const patientData: EmergencyTransportPatientModel = await this.emergencyTransportPatientsService.get(userStore, patient.id, queryParams);
      const font: string = await fontsStore.getPdfFont({ userStore });
      const pdfUtil = new EmergencyTransportPatientPdf(font);
      const dateStr = this.$dateUtil.convertNumberToString(patientData.created || Date.now(), "yyyymmdd");
      const fileName = `救急搬送情報_${patientData.name}_${dateStr}.pdf`;
      pdfUtil.createAndDownloadPdf(patientData, fileName);
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }
}
</script>

<style lang="scss" scoped>
#emergency-transport-patient-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .setting-btn {
    min-width: 95px;
  }
  .add-btn {
    min-width: 95px;
  }
}
</style>
